import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/features/auth/providers/AuthProvider';
import { AuthenticationError } from '@/core/auth/tokenUtil';
import { msalInstance } from '@/core/auth/msalInstance';
import { loginRequest } from '@/config/authConfig';
import { EventType } from '@azure/msal-browser';
import './index.css';
import App from './App.tsx';

// Guard to prevent multiple concurrent login calls
let isRedirecting = false;
function safeRedirect() {
  if (isRedirecting) return;
  if (sessionStorage.getItem('msal_redirect_blocked') === '1') return;
  isRedirecting = true;
  msalInstance.loginRedirect({ ...loginRequest, redirectStartPage: window.location.href })
    .catch(() => { isRedirecting = false; });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 30 * 60 * 1000,   // 30 minutes
      retry: (failureCount, error) => {
        // Never retry on auth errors — redirect instead
        if (error instanceof AuthenticationError) return false;
        return failureCount < 1;
      },
    },
    mutations: {
      onError: (error) => {
        if (error instanceof AuthenticationError) {
          safeRedirect();
        }
      },
    },
  },
});

// Redirect on auth errors from queries globally
queryClient.getQueryCache().subscribe((event) => {
  if (
    event.type === 'updated' &&
    event.query.state.error instanceof AuthenticationError
  ) {
    safeRedirect();
  }
});

// Initialize MSAL before React renders to prevent race conditions
async function prepare() {
  await msalInstance.initialize();

  try {
    const result = await msalInstance.handleRedirectPromise();
    sessionStorage.removeItem('msal_redirect_blocked');
    if (result?.account) {
      msalInstance.setActiveAccount(result.account);
    }
  } catch (error) {
    const e = error as { errorCode?: string; errorNo?: number };
    if (e?.errorCode === 'no_token_request_cache_error') {
      const hasAuthHash = /(?:^|[#&])(code|state|error)=/.test(window.location.hash);
      if (hasAuthHash) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    } else if (e?.errorNo === 9002327) {
      // Prevent endless redirects when browser profile blocks proper SPA token redemption.
      sessionStorage.setItem('msal_redirect_blocked', '1');
      console.error('[Auth] Redirect blocked by AADSTS9002327. Check browser launch flags and Azure SPA registration.', error);
    } else {
      console.error('[Auth] Redirect handling failed:', error);
    }
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event: import('@azure/msal-browser').EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as import('@azure/msal-browser').AuthenticationResult;
      msalInstance.setActiveAccount(payload.account);
    }
  });
}

prepare()
  .then(() => {
    createRoot(document.getElementById('root')!).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </StrictMode>,
    );
  })
  .catch((error) => {
    console.error('Failed to start app:', error);

    document.getElementById('root')!.innerHTML = `
      <div style="padding: 20px; font-family: sans-serif; color: var(--text-primary); background: var(--bg-app); min-height: 100vh;">
        <h1>Unable to load application</h1>
        <p>Please try clearing your browser cache and reloading.</p>
        <p style="font-size: 0.8em; color: var(--text-muted);">Error: ${error.message || error}</p>
      </div>
    `;
  });
